// ============================================================================
// marketFeedLive.ts — ЖИВАЯ ЛЕНТА ЗАЯВОК МАСТЕРА (SSoT клиентской части).
//
// Механизм (без новых proto/RPC, только существующая шина):
//   • подписка на существующие события данных
//     data.document.mkt_requests.{created|updated|deleted} (payload сервера =
//     {table, id, action, actor, ts, row:{значения}}; контакты заявки сервер
//     маскирует ДО публикации) И на карточные события ленты
//     market.request.{created|taken|archived} (плоский payload: id, категория,
//     статус, master_id, contact_revealed, take_price, created_at — контактов
//     в нём нет по построению);
//   • применение — НЕ перезагрузка ленты: карточка добавляется/обновляется/
//     убирается в текущей странице (сортировка по свежести, лимит страницы);
//   • индикатор/звук/бейдж — по настройкам пользователя:
//       notifications.market_requests          — главный выключатель,
//       notifications.market_requests_inapp    — индикатор новых заявок,
//       sound.enabled + sound.notify_enabled   — звук (общий механизм звуков);
//   • при разрыве связи события теряются — вызывающий код делает ТИХУЮ сверку
//     с сервером при подтверждённом online (без полной перезагрузки экрана).
//
// Контакты (телефон/адрес) из широковещательного события не раскрываются:
// карточка показывает состояние contact_revealed, как и чтение списка.
// ============================================================================

import { create } from 'zustand';
import type { TableDataRow } from '@zdc/sdk';
import type { BusEvent } from '@/lib/eventBusHub';
import { settingsStore } from '@/lib/settingsStore';
import { getSoundSettings, playSound } from '@/lib/sound';
import { useAppBadgesStore } from '@/lib/appBadges';
import { createLogger } from '@/lib/logger';
import { logSwallowed } from '@/lib/errors';
import { MARKET_CLIENT_APP_CODE } from '@/lib/marketConstants';
import {
  marketFeedCreatedAtMs,
  marketFeedItemFromRow,
  marketFeedMatchesFilters,
  type MarketFeedAuxiliary,
  type MarketFeedFilters,
  type MarketFeedItem,
  type MarketFeedPage,
  type MarketFeedScope,
} from '@/lib/marketFeed';
import type { MarketGeoRow } from '@/lib/marketGeo';

const log = createLogger('marketFeedLive');

/** Топики событий данных: сервер публикует данные заявок единым механизмом. */
export const MARKET_REQUEST_TOPIC_PREFIX = 'data.document.mkt_requests.';
/** Топики КАРТОЧНЫХ событий ленты (создание/взятие/архив), если их публикует база. */
export const MARKET_FEED_TOPIC_PREFIX = 'market.request.';

export type MarketFeedLiveAction = 'created' | 'updated' | 'deleted';

const LIVE_ACTIONS: readonly MarketFeedLiveAction[] = ['created', 'updated', 'deleted'];

/** Действия карточных событий ленты → действие над карточкой. */
const FEED_TOPIC_ACTIONS: Record<string, MarketFeedLiveAction> = {
  created: 'created',
  taken: 'updated',
  archived: 'deleted',
};

/** Антидребезг звука: пачка одновременно созданных заявок = один сигнал. */
const SOUND_MIN_INTERVAL_MS = 4000;
/** Сколько идентификаторов новых заявок храним для подсветки. */
const MAX_HIGHLIGHT_IDS = 50;

// ---------- Разбор события шины ------------------------------------------------

export interface MarketFeedLiveEvent {
  action: MarketFeedLiveAction;
  /** id заявки (mkt_requests.id) — единственный ключ карточки. */
  id: string;
  /** Значения строки из события (контакты уже замаскированы сервером). */
  row: Map<string, string>;
  /** Кто выполнил изменение (для «своё действие не сигналит»). */
  actorId: string;
  /** Время события (RFC3339, сервер). */
  ts: string;
  topic: string;
}

function objectOf(source: unknown): Record<string, unknown> | null {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
  return source as Record<string, unknown>;
}

/** Строковое поле события (нет поля — пустая строка, без «undefined»). */
function textOf(source: Record<string, unknown> | null, key: string): string {
  const value = source ? source[key] : undefined;
  if (value === null || value === undefined) return '';
  return String(value);
}

function rowCells(source: unknown): Map<string, string> {
  const cells = new Map<string, string>();
  const record = objectOf(source);
  if (!record) return cells;
  for (const [key, raw] of Object.entries(record)) {
    if (raw === null || raw === undefined) continue;
    // Вложенные структуры (JSON-реквизиты) в карточку ленты не выводятся.
    if (typeof raw === 'object') continue;
    const value = String(raw);
    if (!value || value === '<nil>') continue;
    cells.set(key, value);
  }
  return cells;
}

/** Событие принадлежит заявкам маркетплейса? Возвращает разобранное событие. */
export function parseMarketFeedLiveEvent(ev: BusEvent): MarketFeedLiveEvent | null {
  const topic = String(ev?.topic || '');
  const payload = objectOf(ev?.payload);

  // 1) Карточные события ленты (market.request.*): плоский payload без контактов.
  if (topic.startsWith(MARKET_FEED_TOPIC_PREFIX)) {
    const actionKey = topic.slice(MARKET_FEED_TOPIC_PREFIX.length);
    const action = FEED_TOPIC_ACTIONS[actionKey];
    if (!action) return null;
    const id = textOf(payload, 'id') || String(ev?.entityId || '');
    if (!id) return null;
    const cells = rowCells(payload);
    // Представление категории у карточных событий — отдельное поле.
    const categoryName = textOf(payload, 'category_name').trim();
    if (categoryName && !cells.get('category_id_presentation')) {
      cells.set('category_id_presentation', categoryName);
    }
    return {
      action,
      id,
      row: action === 'deleted' ? new Map() : cells,
      actorId: textOf(payload, 'actor'),
      ts: textOf(payload, 'ts'),
      topic,
    };
  }

  // 2) События данных (data.document.mkt_requests.*): полная строка (маскированная).
  if (!topic.startsWith(MARKET_REQUEST_TOPIC_PREFIX)) return null;
  const action = topic.slice(MARKET_REQUEST_TOPIC_PREFIX.length) as MarketFeedLiveAction;
  if (!LIVE_ACTIONS.includes(action)) return null;
  const id = textOf(payload, 'id') || String(ev?.entityId || '');
  if (!id) return null;
  return {
    action,
    id,
    row: action === 'deleted' ? new Map() : rowCells(payload?.row),
    actorId: textOf(payload, 'actor'),
    ts: textOf(payload, 'ts'),
    topic,
  };
}

// ---------- Настройки «живой ленты» --------------------------------------------

export interface MarketFeedLiveSettings {
  /** Главный выключатель (настройка уведомлений о заявках маркетплейса). */
  enabled: boolean;
  /** Индикатор/подсветка новых заявок в ленте. */
  inApp: boolean;
  /** Звук новой заявки (учитывает общие настройки звуков платформы). */
  sound: boolean;
  /** Счётчик на иконке приложения (бейдж дока). */
  badge: boolean;
}

/**
 * Настройки живого обновления. Ключи `notifications.market_requests*` —
 * те же, что использует серверный механизм уведомлений (SSoT пользователя);
 * отсутствие ключа = включено. Звук — общий механизм `sound.*`.
 */
export function getMarketFeedLiveSettings(): MarketFeedLiveSettings {
  const master = settingsStore.getJSON<boolean>('notifications.market_requests', true) !== false;
  const inApp = settingsStore.getJSON<boolean>('notifications.market_requests_inapp', true) !== false;
  const sound = getSoundSettings();
  return {
    enabled: master,
    inApp,
    sound: master && sound.enabled && sound.notify,
    badge: master && inApp,
  };
}

// ---------- Состояние индикатора новых заявок ----------------------------------

interface MarketFeedLiveState {
  /** id заявок, пришедших живьём и ещё не просмотренных (порядок — свежие первыми). */
  newIds: string[];
  /** Добавить новые (уникально, без повторов). */
  note: (ids: string[]) => void;
  /** Убрать из индикатора (заявка взята/удалена/перестала подходить). */
  drop: (ids: string[]) => void;
  /** Оставить только те, что реально есть в ленте (после сверки с сервером). */
  retain: (ids: string[]) => void;
  /** Пользователь увидел новые заявки — индикатор и бейдж обнуляются. */
  acknowledge: () => void;
}

function applyAppBadge(count: number): void {
  if (!getMarketFeedLiveSettings().badge) {
    useAppBadgesStore.getState().setBadge(MARKET_CLIENT_APP_CODE, 0);
    return;
  }
  useAppBadgesStore.getState().setBadge(MARKET_CLIENT_APP_CODE, count);
}

export const useMarketFeedLiveStore = create<MarketFeedLiveState>((set, get) => {
  const commit = (ids: string[]) => {
    const prev = get().newIds;
    const same = ids.length === prev.length && ids.every((id, i) => id === prev[i]);
    if (same) return;
    set({ newIds: ids });
    applyAppBadge(ids.length);
  };
  return {
    newIds: [],
    note: (ids) => {
      const fresh = ids.filter((id) => id && !get().newIds.includes(id));
      if (fresh.length === 0) return;
      commit([...fresh, ...get().newIds].slice(0, MAX_HIGHLIGHT_IDS));
    },
    drop: (ids) => {
      const drop = new Set(ids);
      commit(get().newIds.filter((id) => !drop.has(id)));
    },
    retain: (ids) => {
      const keep = new Set(ids);
      commit(get().newIds.filter((id) => keep.has(id)));
    },
    acknowledge: () => commit([]),
  };
});

// ---------- Звук ----------------------------------------------------------------

let lastSoundAt = 0;

/** Звук новой заявки (по настройкам; пачка событий — один сигнал). */
export function playMarketFeedNewSound(): void {
  const settings = getMarketFeedLiveSettings();
  if (!settings.sound) return;
  const now = Date.now();
  if (now - lastSoundAt < SOUND_MIN_INTERVAL_MS) return;
  lastSoundAt = now;
  void playSound('notify').catch((e) => logSwallowed(e, 'marketFeedLive: звук новой заявки'));
}

/**
 * Отметить новые заявки: индикатор (in-app), бейдж приложения и звук — каждый
 * по своей настройке. Своё действие (actorId === userId) не сигналит звуком.
 */
export function noteNewMarketRequests(
  ids: string[],
  options: { actorId?: string; userId?: string } = {},
): void {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return;
  const settings = getMarketFeedLiveSettings();
  if (!settings.enabled) return;
  const ownAction = !!options.actorId && options.actorId === options.userId;
  if (!ownAction) playMarketFeedNewSound();
  // Индикатор скрыт настройкой, но бейдж приложения всё равно считает новые.
  if (settings.inApp || settings.badge) {
    useMarketFeedLiveStore.getState().note(unique);
  }
}

/** Пользователь увидел ленту — индикатор и бейдж обнуляются. */
export function acknowledgeMarketFeedNew(): void {
  useMarketFeedLiveStore.getState().acknowledge();
}

/** Заявка ушла из ленты (взята/удалена) — убрать подсветку. */
export function dropMarketFeedNew(ids: string[]): void {
  useMarketFeedLiveStore.getState().drop(ids);
}

/** После сверки с сервером — оставить подсветку только для существующих карточек. */
export function retainMarketFeedNew(ids: string[]): void {
  useMarketFeedLiveStore.getState().retain(ids);
}

// ---------- Применение события к странице --------------------------------------

/** Контекст текущего отбора (тот же, что ушёл на сервер при загрузке). */
export interface MarketFeedLiveContext {
  scope: MarketFeedScope;
  userId: string;
  filters: MarketFeedFilters;
}

/**
 * Соответствует ли заявка текущему отбору ленты. ЕДИНЫЙ предикат с серверной
 * загрузкой (marketFeedMatchesFilters): category/service/status/цена/рейтинг/
 * срочность/гео — иначе карточка «застрянет» в списке, где её по правилам
 * быть не должно.
 */
export function matchesMarketFeed(item: MarketFeedItem, ctx: MarketFeedLiveContext): boolean {
  return marketFeedMatchesFilters(item, ctx.scope, ctx.filters);
}

/** Строка события → карточка ленты. null — у события нет данных (удаление). */
export function buildMarketFeedLiveItem(
  event: MarketFeedLiveEvent,
  ctx: MarketFeedLiveContext,
  aux: MarketFeedAuxiliary,
): MarketFeedItem | null {
  if (event.action === 'deleted' || event.row.size === 0) return null;
  const cells = new Map(event.row);
  // В событии может не быть created_at (частичная строка) — время события и
  // есть время создания для новой заявки; без него сортировка ленты сломается.
  if (!cells.get('created_at') && event.ts) cells.set('created_at', event.ts);
  // Событие несёт плоский набор значений (map) — приводим к форме строки
  // таблицы, которую понимает единый построитель карточки. Объект не уходит
  // в protobuf-транспорт, поэтому достаточно двух используемых полей.
  const row = {
    id: event.id,
    values: [...cells.entries()].map(([column, value]) => ({ column, value })),
  } as unknown as TableDataRow;
  return marketFeedItemFromRow(row, ctx.userId, aux.categories, aux.statusLabels);
}

export interface MarketFeedMergeResult {
  page: MarketFeedPage;
  /** В ленте появилась НОВАЯ карточка (created или запись стала подходить). */
  added: boolean;
  /** Карточка ушла из ленты (удалена/перестала подходить). */
  removed: boolean;
  /** Применить событие не удалось (нет данных строки) — нужна сверка с сервером. */
  requiresResync: boolean;
}

function insertNewestFirst(items: MarketFeedItem[], item: MarketFeedItem, limit: number): MarketFeedItem[] {
  const next = [...items];
  const value = marketFeedCreatedAtMs(item.createdAt);
  const index = next.findIndex((existing) => marketFeedCreatedAtMs(existing.createdAt) < value);
  next.splice(index === -1 ? next.length : index, 0, item);
  // Лента — страница: хвост сверх лимита не показываем (total остаётся честным).
  return next.slice(0, Math.max(1, limit));
}

/**
 * Гео после события. Карточные события ленты гео не несут — сохраняем
 * известное. Событие данных содержит полную строку, НО координаты в
 * широковещательных событиях всегда маскируются (markettake/mask.go):
 * раскрытые координаты карточки не затираются маской.
 */
function mergeGeoRows(
  existing: MarketGeoRow,
  fresh: MarketGeoRow,
  fullRow: boolean,
): MarketGeoRow {
  if (!fullRow) return existing;
  return {
    countryId: fresh.countryId,
    regionId: fresh.regionId,
    cityId: fresh.cityId,
    areaId: fresh.areaId,
    lat: fresh.lat ?? existing.lat,
    lon: fresh.lon ?? existing.lon,
    radiusKm: fresh.radiusKm,
  };
}

function mergeItem(
  existing: MarketFeedItem,
  fresh: MarketFeedItem,
  ctx: MarketFeedLiveContext,
  fullRow: boolean,
): MarketFeedItem {
  const merged: MarketFeedItem = {
    ...existing,
    ...fresh,
    number: fresh.number || existing.number,
    categoryName: fresh.categoryName || existing.categoryName,
    serviceId: fresh.serviceId || existing.serviceId,
    masterName: fresh.masterName || existing.masterName,
    descr: fresh.descr || existing.descr,
    createdAt: fresh.createdAt || existing.createdAt,
    createdBy: fresh.createdBy || existing.createdBy,
    statusLabel: fresh.statusLabel || existing.statusLabel,
    takePrice: fresh.takePrice || existing.takePrice,
    takePriceValue: fresh.takePriceValue ?? existing.takePriceValue,
    masterCatalogId: fresh.masterCatalogId || existing.masterCatalogId,
    masterRating: fresh.masterRating ?? existing.masterRating,
    masterReviewsCount: fresh.masterReviewsCount ?? existing.masterReviewsCount,
    isMine: !!ctx.userId && fresh.masterId === ctx.userId,
    // Подписи места не стираются частичным карточным событием (в нём может
    // не быть ссылок иерархии).
    countryName: fresh.countryName || existing.countryName,
    regionName: fresh.regionName || existing.regionName,
    cityName: fresh.cityName || existing.cityName,
    areaName: fresh.areaName || existing.areaName,
    geo: mergeGeoRows(existing.geo, fresh.geo, fullRow),
  };
  // Уже раскрытый мне контакт не затирается маскированным значением события.
  if (existing.contactRevealed && !fresh.contactRevealed) {
    merged.contactRevealed = true;
    merged.phone = existing.phone;
    merged.address = existing.address;
    merged.isTakenByOther = false;
  }
  return merged;
}

/**
 * Применить живое событие к ТЕКУЩЕЙ странице ленты (без полной перезагрузки):
 * добавление в отсортированное место, обновление карточки, удаление.
 */
export function mergeMarketFeedLiveEvent(
  page: MarketFeedPage,
  event: MarketFeedLiveEvent,
  ctx: MarketFeedLiveContext,
  item: MarketFeedItem | null,
  limit: number,
): MarketFeedMergeResult {
  const items = page?.items || [];
  const total = Math.max(0, Number(page?.total || 0));
  const index = items.findIndex((existing) => existing.id === event.id);

  if (event.action === 'deleted') {
    if (index === -1) {
      return { page, added: false, removed: false, requiresResync: false };
    }
    const nextItems = items.filter((existing) => existing.id !== event.id);
    return {
      page: { items: nextItems, total: Math.max(nextItems.length, total - 1) },
      added: false,
      removed: true,
      requiresResync: false,
    };
  }

  if (!item) {
    return { page, added: false, removed: false, requiresResync: true };
  }

  const fullRow = event.topic.startsWith(MARKET_REQUEST_TOPIC_PREFIX);
  // Отбор проверяется на ИТОГОВОЙ карточке: событие может быть частичным
  // (например, смена статуса без take_price) — известные значения сохраняются.
  const effective = index === -1 ? item : mergeItem(items[index], item, ctx, fullRow);
  const matches = matchesMarketFeed(effective, ctx);

  if (index === -1) {
    if (!matches) {
      return { page, added: false, removed: false, requiresResync: false };
    }
    const nextItems = insertNewestFirst(items, item, limit);
    return {
      page: { items: nextItems, total: Math.max(total + 1, nextItems.length) },
      added: event.action === 'created',
      removed: false,
      requiresResync: false,
    };
  }

  if (!matches) {
    const nextItems = items.filter((existing) => existing.id !== event.id);
    return {
      page: { items: nextItems, total: Math.max(nextItems.length, total - 1) },
      added: false,
      removed: true,
      requiresResync: false,
    };
  }

  const nextItems = [...items];
  nextItems[index] = effective;
  log.debug(`live мкт-заявка ${event.id} обновлена (${event.action})`);
  return {
    page: { items: nextItems, total },
    added: false,
    removed: false,
    requiresResync: false,
  };
}
